<template>
  <div class="box" :style="subtitlePosition">
    <div ref="refContainerSubtitle" class="highlight-bg" :style="styleContainerScroll">
      <img
        v-if="userStore.userPreferences.subtitleIsVisibleLogo"
        :src="getAssetImageUrl('common/logos/logo-sconai-full-timer-white.png')"
        class="scon-logo"
        :style="styleLogo"
      />

      <div ref="refGrowingElevator" :style="styleGrowingElevator">
        <p
          ref="refSubtitleTotal"
          :class="['medium', 'oc-dim', 'translated-text']"
          :style="styleSubtitle"
          :dir="isRightToLeft ? 'rtl' : 'ltr'"
        >
          <span :class="['highlight-translated']" :dir="isRightToLeft ? 'rtl' : 'ltr'">
            {{ `${textThoroughlyComplete}${splitter}` }}
          </span>

          <template
            v-for="(messageDetails, index) in contentsLast"
            :key="`${messageDetails.chatSeqSubId}-${messageDetails.highlight}`"
          >
            <span
              v-if="!messageDetails.highlight || index !== contentsLast.length - 1"
              :class="['highlight-translated']"
              :dir="isRightToLeft ? 'rtl' : 'ltr'"
            >
              {{ messageDetails.content }}
            </span>

            <AnimationTyping
              :is-visible="messageDetails.highlight && index === contentsLast.length - 1"
              :text="messageDetails.content"
              :is-keep-all="!isLanguageNoSpace(messageDetails.languageCode)"
            />
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, CSSProperties, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { debounce } from 'lodash';

import AnimationTyping from '@/components/animations/AnimationTyping.vue';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { ChatMessage } from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { ij_resetSubtitle } from '@/utils/injectionKeys.js';
import useHex from '@/utils/useHex';
import useQueue from '@/utils/useQueue';

const queueOperation = useQueue();

const audioStore = useAudioStore();
const userStore = useUserStore();

const props = withDefaults(
  defineProps<{
    messageList: ChatMessage[];
    position: 'top' | 'bottom';
  }>(),
  {
    messageList: () => [],
  }
);

const resetSubtitle = inject(ij_resetSubtitle) as () => void;

const refSubtitleTotal = ref<HTMLElement>();
const refContainerSubtitle = ref<HTMLElement>();
const refGrowingElevator = ref<HTMLElement>();
const lineHeight = ref(0);
const textThoroughlyComplete = ref(''); // 확정 완료 문구 모음
const chatLogIdPointer = ref('');
const elevatorMinHeight = ref(0); // px
const styleLogo = ref({}); // 로고 스타일 반환
let timeoutSubtitleReset = 0;
let lastElevatorHeight = 0;
let resizeObserver: ResizeObserver | null = null;

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight = ['ar']; // 아랍어,
  return listLangAlignRight.includes(audioStore.languageSelected?.code ?? '');
});

// 자막 컨테이너 높이 계산
const styleContainerScroll = computed<CSSProperties>(() => {
  const boxHeight = lineHeight.value * userStore.userPreferences.subtitleLineCount;
  const heightContainer = `calc(${boxHeight}px + var(--subtitle-extra))`;
  return {
    backgroundColor: useHex('#000000', userStore.userPreferences.subtitleOpacity),
    width: `${userStore.userPreferences.subtitleWidth}%`,
    maxHeight: heightContainer,
    minHeight: heightContainer,
    textAlign: 'start',
  };
});

// 검은 상자 높이 반환
const heightBoxBlack = computed(() => lineHeight.value * userStore.userPreferences.subtitleLineCount);

// 자막 스타일 반환(너비, 줄 높이)
const styleSubtitle = computed(() => {
  const el = refContainerSubtitle.value as HTMLElement | null;
  if (!el) return {};
  return {
    lineHeight: userStore.userPreferences.subtitleLineHeight,
    wordBreak: audioStore.isLanguageNoSpace ? 'break-all' : 'keep-all',
  };
});

const subtitlePosition = computed(() => {
  return {
    [props.position]: `${userStore.userPreferences.subtitleLocationY}vh`,
    '--font-multiplier': String(userStore.userPreferences.textSizeMultiplier),
  };
});

// 마지막 메세지의 마지막 subId
const contentsLast = computed(() => {
  const messageLast = props.messageList.findLast((msg) => msg.tgt.contents.length);
  if (!messageLast) return [];
  return messageLast.tgt.contents;
});

// 띄어쓰기
const splitter = computed(() => (audioStore.isLanguageNoSpace ? '' : ' '));

// 자막 컨테이너 스타일 반환
const styleGrowingElevator = computed<CSSProperties>(() => {
  const styles: CSSProperties = {
    // 자막이 커질수록 위로 이동
    transform: `translateY(${heightBoxBlack.value - elevatorMinHeight.value}px)`,
    willChange: 'transform',
  };

  if (elevatorMinHeight.value > 0) {
    styles.minHeight = `${elevatorMinHeight.value}px`;
  }

  styles.transition = 'transform 300ms ease-out';

  return styles;
});

// 로고 스타일 반환
const updateStyleLogo = () => {
  const el = refSubtitleTotal.value;
  if (!el) return;
  const cs = getComputedStyle(el);
  const fs = parseFloat(cs.fontSize);
  const heightRem = 1.5;
  const heightPx = heightRem * 16;
  const bottom = fs + (fs * userStore.userPreferences.subtitleLineHeight) / 2 - heightPx / 4;

  styleLogo.value = {
    height: `${heightRem}rem`,
    bottom: `${bottom}px`,
    right: '1.5rem',
  };
};

// 현재 개별 줄 높이 반환
const getLineHeight = () => {
  const el = refSubtitleTotal.value;
  if (!el) return;
  const cs = getComputedStyle(el);
  const lh = parseFloat(cs.lineHeight);
  if (Number.isFinite(lh)) {
    lineHeight.value = lh;
    return lh;
  }
  const fs = parseFloat(cs.fontSize) || 16;
  lineHeight.value = fs * userStore.userPreferences.subtitleLineHeight;
  return lineHeight.value;
};

const handleChangeMessageList = () => {
  const messageLast = props.messageList.at(-1);
  const isFirst = !textThoroughlyComplete.value;
  if (!messageLast) return;
  if (!messageLast.tgt.contents.length) return;
  if (messageLast?.callLogId === chatLogIdPointer.value) return;
  const messagePenultimate = props.messageList.at(-2);
  if (!messagePenultimate) return;
  const contentsPenultimate = messagePenultimate.tgt.contents.map((item) => item.content).join(splitter.value);
  textThoroughlyComplete.value += `${isFirst ? '' : splitter.value}${contentsPenultimate}`;
  chatLogIdPointer.value = messageLast.callLogId;
};

// 마지막 자막 업데이트 시점에서 10초 지나면 자막 비우기
const setTimeoutSubtitleReset = () => {
  clearTimeout(timeoutSubtitleReset);
  timeoutSubtitleReset = setTimeout(() => {
    reset();
  }, 10000);
};

// 초기화
const reset = () => {
  resetSubtitle();
  textThoroughlyComplete.value = '';
  initializeElevatorMinHeight(); // 이전 자막 높이 초기화
  lastElevatorHeight = 0;
};

// 자막 컨테이너 크기변경 옵저버
const setupResizeObserver = () => {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const height = entry.contentRect.height;
      handleElevatorResize(height);
    }
  });

  if (refGrowingElevator.value) {
    resizeObserver.observe(refGrowingElevator.value);
  }
};

// 자막 컨테이너가 커졌을 때 처리
const handleElevatorResize = (newHeight: number) => {
  if (!Number.isFinite(newHeight) || newHeight <= 0) return;

  // 높이가 커진 경우 (새 텍스트 추가)
  if (newHeight > lastElevatorHeight) {
    // 새로운 크기를 최소값으로 지정
    elevatorMinHeight.value = newHeight;
  }

  lastElevatorHeight = newHeight;
};

const initializeElevatorMinHeight = () => {
  elevatorMinHeight.value = heightBoxBlack.value;
};

const onWindowResize = debounce(async () => {
  elevatorMinHeight.value = 0;
  await nextTick();

  const height = refGrowingElevator.value?.getBoundingClientRect().height;
  if (typeof height !== 'number') return;

  elevatorMinHeight.value = Math.max(height, heightBoxBlack.value ?? 0);
}, 100);

onMounted(async () => {
  setupResizeObserver();
  updateStyleLogo();
  await nextTick();
  getLineHeight();
  initializeElevatorMinHeight();
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  window.removeEventListener('resize', onWindowResize);
});

// 자동 스크롤
watch(
  () => props.messageList,
  async () => {
    queueOperation.add(async () => {
      handleChangeMessageList();
      await nextTick();
      setTimeoutSubtitleReset(); // 마지막 자막 업데이트 시점에서 10초 지나면 자막 비우기
    });
  },
  { deep: true, flush: 'post' }
);

// 자막 너비 변경 시
watch(
  [
    () => userStore.userPreferences.subtitleWidth,
    () => userStore.userPreferences.textSizeMultiplier,
    () => userStore.userPreferences.subtitleLineHeight,
  ],
  () => {
    getLineHeight();
    updateStyleLogo();

    initializeElevatorMinHeight(); // 이전 자막 높이 초기화
    lastElevatorHeight = 0;
  },
  { flush: 'post', immediate: true }
);
</script>

<style scoped lang="scss">
.highlight-bg {
  --subtitle-extra: 0.5rem;
  padding: 0 1.5rem var(--subtitle-extra);
  position: relative;
}

/* 번역본 - 번역/입력중인 텍스트 */
.highlight-translated {
  display: inline;
  color: var(--font-dimmest);
  animation: none;
}

.box {
  position: absolute;
  display: flex;
  margin: 0 auto;
  width: 100%;
  justify-content: center;
  padding: 0 1.25rem 0 1.25rem;
  overflow: hidden;
}

/* 번역본 - 텍스트 */
.translated-text {
  font-size: calc(var(--font-size-h3) * var(--font-multiplier, 1));
  line-height: 150%;
  word-break: keep-all;
  overflow-wrap: break-word;
  unicode-bidi: isolate;
  text-align: start;
}

.scon-logo {
  position: absolute;

  opacity: 0.3;
  pointer-events: none;
  z-index: 0;
  object-fit: contain;
}
</style>
